import {useMemo} from "react";

import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import {Badge} from "@/components/ui/badge";
import {formatDate} from "@/src/modules/core/lib/formatters";
import type {VehicleSafelistingsAccountability} from "@/src/modules/vehicles/types";

import {
  alignScheduleToPermitDays,
  getEarliestPermitReleaseDate,
  getPermitReleaseSchedule,
  toPermitDateKey,
  type PermitReleaseEntry,
} from "../lib/get-permit-release-schedule";

type LimitUsagePanelProps = {
  id?: string;
  usedSources: NonNullable<VehicleSafelistingsAccountability["used_sources"]>;
  rollingDays: number | null;
  nextReset: string | null;
};

type UsedSourceItem = NonNullable<
  VehicleSafelistingsAccountability["used_sources"]
>["items"][number];

type ItemSchedule = {
  item: UsedSourceItem;
  schedule: PermitReleaseEntry[];
};

function buildItemSchedules(items: UsedSourceItem[], rollingDays: number | null): ItemSchedule[] {
  if (rollingDays == null) return [];
  return items.map((item) => {
    const full = getPermitReleaseSchedule(item.starts_at, item.ends_at, rollingDays);
    return {
      item,
      schedule: alignScheduleToPermitDays(full, item.days),
    };
  });
}

export function LimitUsagePanel({
  id,
  usedSources,
  rollingDays,
  nextReset,
}: LimitUsagePanelProps) {
  const sortedItems = useMemo(
    () =>
      [...usedSources.items].sort(
        (a, b) =>
          new Date(b.starts_at).getTime() - new Date(a.starts_at).getTime() ||
          new Date(b.ends_at).getTime() - new Date(a.ends_at).getTime(),
      ),
    [usedSources.items],
  );

  const itemSchedules = useMemo(
    () => buildItemSchedules(sortedItems, rollingDays),
    [rollingDays, sortedItems],
  );

  const nextReleaseKey = useMemo(() => {
    const fromTable = getEarliestPermitReleaseDate(itemSchedules.map(({schedule}) => schedule));
    return fromTable ?? (nextReset ? toPermitDateKey(nextReset) : null);
  }, [itemSchedules, nextReset]);

  const subtitleParts: string[] = [];
  if (rollingDays != null) {
    subtitleParts.push(
      `Each counted safelisting day uses your limit for ${rollingDays} days`,
    );
  }
  if (nextReleaseKey) {
    subtitleParts.push(`Next permit available ${formatDate(nextReleaseKey)}`);
  }
  const subtitle = subtitleParts.join(" · ");

  return (
    <section
      id={id}
      aria-label="Active safelistings using your limit"
      className="border-border/60 bg-muted/30 flex flex-col gap-3 rounded-md border px-3 py-3"
    >
      <div className="flex flex-col gap-1">
        <h3 className="text-foreground text-sm font-medium leading-snug">
          Active safelistings using your limit
        </h3>
        {subtitle ? (
          <p className="text-muted-foreground text-pretty text-xs leading-snug break-words">
            {subtitle}
          </p>
        ) : null}
      </div>

      {itemSchedules.length === 0 ? (
        <p className="text-muted-foreground py-2 text-center text-sm">
          No active safelistings in this period.
        </p>
      ) : (
        <Accordion multiple defaultValue={[]} className="border-border/60 w-full min-w-0 rounded-md border bg-background">
          {itemSchedules.map(({item, schedule}) => (
            <LimitUsageAccordionItem
              key={item.safelisting_id}
              item={item}
              schedule={schedule}
            />
          ))}
        </Accordion>
      )}

      {usedSources.truncated ? (
        <p className="text-muted-foreground text-center text-xs">
          Showing the {usedSources.safelisting_count} most recent.
        </p>
      ) : null}
    </section>
  );
}

function LimitUsageAccordionItem({
  item,
  schedule,
}: {
  item: UsedSourceItem;
  schedule: PermitReleaseEntry[];
}) {
  const itemValue = String(item.safelisting_id);
  const vehicleLabel = item.vehicle_label?.trim() || `Vehicle #${item.vehicle_id}`;

  return (
    <AccordionItem value={itemValue} className="min-w-0 px-3">
      <AccordionTrigger className="min-w-0 gap-2 py-3 hover:no-underline">
        <div className="flex min-w-0 flex-1 flex-col gap-2 sm:flex-row sm:items-start sm:justify-between sm:gap-3">
          <div className="min-w-0 text-left">
            <p className="text-foreground truncate text-sm font-medium">{vehicleLabel}</p>
            <p className="text-muted-foreground mt-0.5 text-xs">
              {formatDate(item.starts_at)} – {formatDate(item.ends_at)}
            </p>
          </div>
          <Badge variant="secondary" className="w-fit shrink-0 tabular-nums">
            {item.days} {item.days === 1 ? "permit day" : "permit days"}
          </Badge>
        </div>
      </AccordionTrigger>

      {schedule.length > 0 ? (
        <AccordionContent className="!h-auto pb-3">
          <PermitReleaseScheduleTable schedule={schedule} />
        </AccordionContent>
      ) : null}
    </AccordionItem>
  );
}

function PermitReleaseScheduleTable({schedule}: {schedule: PermitReleaseEntry[]}) {
  return (
    <div
      className="flex flex-col gap-1.5 text-sm leading-snug"
      role="table"
      aria-label="When each counted permit day becomes available again"
    >
      <div className="text-muted-foreground border-border/60 grid grid-cols-2 gap-x-3 border-b pb-1.5 text-sm font-medium">
        <span role="columnheader">Counted day</span>
        <span role="columnheader">Available again</span>
      </div>
      <ul className="flex flex-col text-sm" role="rowgroup">
        {schedule.map((entry) => (
          <PermitReleaseScheduleRow key={entry.usedOn} entry={entry} />
        ))}
      </ul>
    </div>
  );
}

function PermitReleaseScheduleRow({entry}: {entry: PermitReleaseEntry}) {
  return (
    <li
      role="row"
      className="border-border/40 grid grid-cols-2 gap-x-3 border-b py-2 text-sm leading-snug last:border-b-0"
    >
      <span role="cell" className="text-foreground tabular-nums">
        {formatDate(entry.usedOn)}
      </span>
      <span role="cell" className="text-muted-foreground tabular-nums">
        {formatDate(entry.releasesOn)}
      </span>
    </li>
  );
}
